'use client';

/**
 * Гард защищённых экранов.
 *
 * Главное различие, ради которого он написан: 401 и 403 — разные события.
 * 401 — сессии нет, надо входить. 403 — человек уже вошёл, и отправлять его
 * логиниться повторно бессмысленно: второй вход ничего не изменит, а
 * выглядит это как сломанный вход, хотя сломан доступ.
 */

import React, { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { Loader2, ShieldAlert, ServerCrash, KeyRound } from 'lucide-react';
import { ApiError } from '@/lib/api/client';
import { MeProvider, useMeQuery } from './me';
import { missingOidcConfig, type SigninState } from './config';

function Screen({
  icon,
  title,
  children,
  actions,
}: {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center">
        <div className="flex justify-center mb-4 text-slate-400">{icon}</div>
        <h1 className="text-base font-semibold text-slate-800">{title}</h1>
        {children && <div className="mt-2 text-sm text-slate-500 leading-relaxed">{children}</div>}
        {actions && <div className="mt-6 flex justify-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

function Button({
  onClick,
  children,
  variant = 'primary',
}: {
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'ghost';
}) {
  const cls =
    variant === 'primary'
      ? 'bg-brand text-on-fill hover:bg-brand-light'
      : 'border border-slate-200 text-slate-600 hover:bg-slate-50';
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${cls}`}
    >
      {children}
    </button>
  );
}

function Loading({ note }: { note?: string }) {
  return (
    <Screen icon={<Loader2 size={28} className="animate-spin" />} title="Загрузка">
      {note}
    </Screen>
  );
}

export default function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const missing = missingOidcConfig();
  const configured = missing.length === 0;

  const signIn = () =>
    void auth.signinRedirect({
      state: { returnTo: window.location.pathname + window.location.search } satisfies SigninState,
    });

  // Сессии нет — уводим на вход сами, без экрана «нажмите, чтобы войти»:
  // выбора у человека всё равно нет.
  const needsSignin =
    configured && !auth.isLoading && !auth.isAuthenticated && !auth.error && !auth.activeNavigator;

  useEffect(() => {
    if (needsSignin) signIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSignin]);

  const me = useMeQuery(auth.isAuthenticated);

  if (!configured) {
    return (
      <Screen icon={<ServerCrash size={28} />} title="Вход не настроен">
        Не заданы переменные окружения:{' '}
        <span className="font-mono text-xs text-slate-700">{missing.join(', ')}</span>. Проверьте
        <span className="font-mono text-xs"> .env</span> и перезапустите дев-сервер.
      </Screen>
    );
  }

  if (auth.isLoading || auth.activeNavigator) return <Loading />;

  if (auth.error) {
    return (
      <Screen
        icon={<KeyRound size={28} />}
        title="Не удалось войти"
        actions={<Button onClick={signIn}>Попробовать снова</Button>}
      >
        <p>{auth.error.message}</p>
        <p className="mt-2 text-xs text-slate-400">
          Если сообщение про redirect_uri — адрес{' '}
          <span className="font-mono">{process.env.NEXT_PUBLIC_OIDC_REDIRECT_URI}</span> не заведён
          в приложении Zitadel.
        </p>
      </Screen>
    );
  }

  if (!auth.isAuthenticated) return <Loading note="Переходим на страницу входа…" />;

  if (me.isPending) {
    return <Loading note="Первый вход нового пользователя занимает чуть дольше обычного." />;
  }

  if (me.error instanceof ApiError) {
    /**
     * Прав нет либо организация не подключена к тенанту. Это не сбой входа:
     * человек вошёл. Отправлять его логиниться заново нельзя — надо сказать,
     * к кому идти.
     */
    if (me.error.isForbidden) {
      return (
        <Screen
          icon={<ShieldAlert size={28} />}
          title="Доступ не открыт"
          actions={
            <Button variant="ghost" onClick={() => void auth.signoutRedirect()}>
              Выйти
            </Button>
          }
        >
          <p>{me.error.message}</p>
          <p className="mt-2">
            Вход выполнен, но доступа к данным нет. Обратитесь к администратору Callog — повторный
            вход ничего не изменит.
          </p>
        </Screen>
      );
    }

    /**
     * Токена нет на нашей стороне — сервер этого запроса не видел. Значит
     * сессия потерялась между вкладками или истекла, и подсказки про JWT
     * здесь были бы враньём: бэкенд ничего не отвергал.
     */
    if (me.error.isMissingSession) {
      return (
        <Screen
          icon={<KeyRound size={28} />}
          title="Сессия потеряна"
          actions={<Button onClick={signIn}>Войти заново</Button>}
        >
          Токен не найден в этой вкладке. Такое бывает после её перезапуска — повторный вход обычно
          проходит без ввода пароля.
        </Screen>
      );
    }

    /**
     * Вход прошёл, а API токен не принял. На практике это одно из двух:
     * в приложении Zitadel выбран непрозрачный токен вместо JWT, либо в
     * scope не попал audience проекта.
     */
    if (me.error.isUnauthorized) {
      return (
        <Screen
          icon={<KeyRound size={28} />}
          title="API не принял токен"
          actions={
            <>
              <Button onClick={signIn}>Войти заново</Button>
              <Button variant="ghost" onClick={() => void auth.removeUser()}>
                Сбросить сессию
              </Button>
            </>
          }
        >
          <p>{me.error.message}</p>
          <p className="mt-2 text-xs text-slate-400">
            Проверьте в приложении Zitadel тип токена — должен быть JWT, не opaque, — и что в scope
            попал audience проекта.
          </p>
        </Screen>
      );
    }

    return (
      <Screen
        icon={<ServerCrash size={28} />}
        title="Бэкенд недоступен"
        actions={<Button onClick={() => void me.refetch()}>Повторить</Button>}
      >
        {me.error.message}
      </Screen>
    );
  }

  if (!me.data) return <Loading />;

  return <MeProvider me={me.data}>{children}</MeProvider>;
}
